import { SelectorsFormsValues } from "@components/constructor/components/core/forms/selectors";
import { useDefaultSelectorFormValues, usePromisedDispatch } from "@hooks/helperHooks";
import { userActions } from "@store/user";
import { Form, Formik } from "formik";
import {FC, PropsWithChildren, useCallback, useMemo} from "react";
import { toastr } from "@/utils/toastHelper";
import { useNavigate } from "react-router";
import * as Yup from 'yup';
import {FormHelper} from "@constructor/components/core/forms";
import {useLocalizedBlock} from "@services/hooks";

interface CreateSupportTopicFormValues extends SelectorsFormsValues {
    title: string;
    message: string;
}

const CreateSupportTopicForm: FC<PropsWithChildren> = ({children, ...props}) => {

    const navigate = useNavigate();
    const dispatch = usePromisedDispatch();
    const selectors = useDefaultSelectorFormValues();
    
    const globalYup = useLocalizedBlock('yup.message.errors');
    const formMessages = useLocalizedBlock('forms.support.create');

    const initial: CreateSupportTopicFormValues = {
        title: '',
        message: '',
        ...selectors
    }

    const validation = useMemo(() => Yup.object().shape({
        title: Yup.string()
            .min(5, globalYup['notless'].replace('{min}', '5'))
            .max(256, globalYup['notmore'].replace('{max}', '250'))
            .required(globalYup['required']),
        message: Yup.string()
            .min(5, globalYup['notless'].replace('{min}', '5'))
            .max(1000, globalYup['notmore'].replace('{max}', '1000'))
            .required(globalYup['required'])
    }), [globalYup]);

    const onSubmit = useCallback(async (values: CreateSupportTopicFormValues) => {

        const result = await dispatch(userActions.createTask({
            name: values.title,
            message: values.message,
            accountId: values.selected_account || undefined,
            characterId: values.selected_character || undefined,
            gameServerId: values.selected_game_server || undefined,
            loginServerId: values.selected_login_server || undefined
        }));

        if (result.success) {
            toastr.success(formMessages['toast.title'], formMessages['toast.success']);
            navigate(`/support/${result.data.id}/`)
        }
        else {
            if (result.errorCode === 'MaxCount') {
                toastr.error(formMessages['toast.title'], formMessages['toast.errors.max_count']);
            }
            else if (result.errorCode === 'InvalidModel') {
                toastr.error(formMessages['toast.title'], formMessages['toast.errors.invalid_model']);
            }
            else {
                toastr.error(formMessages['toast.title'], formMessages['toast.errors.unknown']);
            }
        }
    }, [dispatch, navigate, formMessages]);

    return (
        <Formik
            initialValues={initial}
            validationSchema={validation}
            onSubmit={onSubmit}>
                <Form {...props}>
                    <FormHelper>
                        {children}
                    </FormHelper>
                </Form>
        </Formik>
    );
}

export default CreateSupportTopicForm;